import {
  BranchesService,
  JobsService,
  MealsService,
  MonitoringService,
  OrdersService,
  PackagesService,
  SearchesService
} from '@/backend/market'

import { TrackOrderRepository } from '../../domain/repositories/TrackOrderRepository'
import { JobLiveModel } from '../models/JobLiveModel'
import type { JobLiveEntity } from '../../domain/entities/JobLiveEntity'
import type { DriverStatus } from '@/features/driver/domain/enums/DriverStatus'
import { JobLiveRouteModel } from '../models/JobLiveRouteModel'
import type { JobLiveRouteEntity } from '../../domain/entities/JobLiveRouteEntity'
import { SearchDeliveryModel } from '../models/SearchDeliveryModel'
import type { SearchDeliveryEntity } from '../../domain/entities/SearchDeliveryEntity'
import type { ShipmentTrackFilterEntity } from '../../domain/entities/ShipmentTrackFilterEntity'
import { ShipmentTrackFilterModel } from '../models/ShipmentTrackFilterModel'
import type { RegionMiniEntity } from '@/features/region/domain/entities/RegionMiniEntity'
import { JobLiveResponseModel } from '../models/JobLiveResponseModel'
import type { JobLiveResponseEntity } from '../../domain/entities/JobLiveResponseEntity'
import { ActiveBranchModel } from '../models/ActiveBranchModel'
import type { ActiveBranchEntity } from '../../domain/entities/ActiveBranchEntity'
import { OrderStatus } from '@/features/order/domain/enums/OrderStatus'
import { OrderLiveResponseModel } from '../models/orderModels/OrderLiveResponseModel'
import type { OrderLiveResponseEntity } from '../../domain/entities/orderEntities/OrderLiveResponseEntity'
import type { ChannelMiniEntity } from '@/features/channel/domain/entities/ChannelMiniEntity'
import { MealMonitoringResponseModel } from '../models/MealMonitoringResponseModel'
import { MealMonitoringFilterModel } from '../models/MealMonitoringFilterModel'
import type { MealMonitoringResponseEntity } from '../../domain/entities/MealMonitoringResponseEntity'
import type { MealMonitoringFilterEntity } from '../../domain/entities/MealMonitoringFilterEntity'
import type { PackageMonitoringFilterEntity } from '../../domain/entities/packageEntites/PackageMonitoringFilterEntity'
import { PackageMonitoringFilterModel } from '../models/packageModels/PackageMonitoringFilterModel'
import { PackageMonitoringResponseModel } from '../models/packageModels/PackageMonitoringResponseModel'
import type { PackageMonitoringResponseEntity } from '../../domain/entities/packageEntites/PackageMonitoringResponseEntity'
import type { PackageMonitoringRouteEntity } from '../../domain/entities/packageEntites/PackageMonitoringRouteEntity'
import { PackageMonitoringRouteModel } from '../models/packageModels/PackageMonitoringRouteModel'
import type { GetMonitoringCountsAdminResponse } from '@/backend/market'

export class TrackOrderRepositoryImpl implements TrackOrderRepository {
  async jobStartMonitoring(
    filter: ShipmentTrackFilterEntity
  ): Promise<JobLiveResponseEntity | { error: true }> {
    if (!filter.channels || filter.channels?.length === 0) return { error: true }
    const shipmentTrackFilterModel = new ShipmentTrackFilterModel(filter)
    try {
      const response = await JobsService.getMonitoringJobsAdmin(shipmentTrackFilterModel)
      const jobLiveResponseModel = new JobLiveResponseModel(response)
      return jobLiveResponseModel
    } catch {
      return { error: true }
    }
  }
  async mealStartMonitoring(
    filter: MealMonitoringFilterEntity
  ): Promise<MealMonitoringResponseEntity | { error: true }> {
    const shipmentTrackFilterModel = new MealMonitoringFilterModel(filter)
    try {
      const response = await MealsService.getMealsMonitoringAdmin(shipmentTrackFilterModel)
      const jobLiveResponseModel = new MealMonitoringResponseModel(response)
      return jobLiveResponseModel
    } catch {
      return { error: true }
    }
  }
  async jobStartMonitoringById(id: string): Promise<JobLiveEntity | { error: true }> {
    try {
      const response = await JobsService.getMonitoringJobAdmin({ id })
      const jobLiveModel = new JobLiveModel(response)
      return jobLiveModel
    } catch {
      return { error: true }
    }
  }

  async orderStartMonitoring(filterData: {
    deliveryStatuses: OrderStatus[]
    regions: RegionMiniEntity[]
    update?: boolean
    dateRange?: { startDate?: string; endDate?: string }
    channels?: ChannelMiniEntity[]
  }): Promise<OrderLiveResponseEntity | { error: true }> {
    try {
      if (!filterData.channels || filterData.channels?.length === 0) return { error: true }

      const response = await OrdersService.getMonitorOrdersAdmin({
        fetchAll: filterData.update ? false : true,
        orderStatuses: [OrderStatus.ACCEPTED],
        regionIds: filterData.regions.map((region) => region.id!) ?? [],
        fromDate: filterData.dateRange?.startDate,
        toDate: filterData.dateRange?.endDate,
        channelIds: filterData.channels?.map((channel) => channel.id!) ?? []
      })
      const orderLiveResponseModel = new OrderLiveResponseModel(response)

      return orderLiveResponseModel
    } catch {
      return { error: true }
    }
  }
  async getDriversMonitoring(driverStatuses: DriverStatus[]): Promise<[] | { error: true }> {
    try {
      // const response = await connection.invoke('GetDriversMonitoringOptions', driverStatuses)

      return driverStatuses as []
    } catch {
      return { error: true }
    }
  }
  async getActiveBranches(
    regionIds?: number[],
    status?: OrderStatus[],
    channels?: ChannelMiniEntity[]
  ): Promise<ActiveBranchEntity[] | { error: true }> {
    try {
      if (!channels || channels?.length === 0) return { error: true }
      const response = await BranchesService.getWorkingBranchesAdmin({
        regionIds: regionIds ?? [],
        orderStatuses: status,
        channelIds: channels?.map((channel) => channel.id!) ?? []
      })
      const activeBranchModel = response.branches?.map((branch) => new ActiveBranchModel(branch))
      return activeBranchModel ?? []
    } catch {
      return { error: true }
    }
  }
  async getJobRoutes(id: string): Promise<JobLiveRouteEntity | { error: true }> {
    try {
      const response = await JobsService.getShipmentsMonitoringRoutesAdmin({
        id
      })
      const jobLiveRouteModel = new JobLiveRouteModel(response as JobLiveRouteModel)
      return jobLiveRouteModel
    } catch {
      return { error: true }
    }
  }
  async getPackageRoutes(id: string): Promise<PackageMonitoringRouteEntity | { error: true }> {
    try {
      const response = await PackagesService.getCargosMonitoringRoutesAdmin({
        id
      })
      const packageMonitoringRouteModel = new PackageMonitoringRouteModel(response)
      return packageMonitoringRouteModel
    } catch {
      return { error: true }
    }
  }
  async getMonitoringCounts(filter?: {
    regionIds?: number[]
    channelIds?: number[]
  }): Promise<GetMonitoringCountsAdminResponse | { error: true }> {
    try {
      return await MonitoringService.getCountsAdmin({
        regionIds: filter?.regionIds,
        channelIds: filter?.channelIds
      })
    } catch {
      return { error: true }
    }
  }
  async getDeliveryBoundaries(orderId: string): Promise<SearchDeliveryEntity | { error: true }> {
    try {
      const response = await SearchesService.getLastSearchAdmin({
        orderId
      })
      const searchDeliveryModel = new SearchDeliveryModel(response.search!)
      return searchDeliveryModel
    } catch {
      return { error: true }
    }
  }
  async broadCastMeal(mealId: string): Promise<{ success: true } | { error: true }> {
    try {
      await MealsService.postBroadcastDeliveryAdmin({
        id: mealId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async stopBroadCastMeal(mealId: string): Promise<{ success: true } | { error: true }> {
    try {
      await SearchesService.postSearchFinishByMealAdmin({
        mealId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async finishDeliverySearching(searchId: string): Promise<{ success: true } | { error: true }> {
    try {
      await SearchesService.postSearchFinishAdmin({
        searchId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async finishDeliverySearchingByOrderId(
    orderId: string
  ): Promise<{ success: true } | { error: true }> {
    try {
      await SearchesService.postSearchFinishByOrderAdmin({
        orderId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async packageStartMonitoring(
    filter: PackageMonitoringFilterEntity
  ): Promise<PackageMonitoringResponseEntity | { error: true }> {
    const packageMonitoringFilterModel = new PackageMonitoringFilterModel(filter)
    try {
      const response = await PackagesService.getMonitoringPackagesAdmin(
        packageMonitoringFilterModel
      )
      const packageMonitoringResponseModel = new PackageMonitoringResponseModel(response)
      return packageMonitoringResponseModel
    } catch {
      return { error: true }
    }
  }
}
